/* eslint-disable no-plusplus */
import {
  CircleTickIcon,
  RadioCheckedIcon,
  RadioUncheckedIcon,
} from '@commenda/icons';
import { classNames } from '@commenda/utils';
import { Icons } from '../Icons';

export enum TTimelineItemStatusLegacy {
  COMPLETED = 'COMPLETED',
  NOT_STARTED = 'NOT_STARTED',
  ACTIVE = 'ACTIVE',
  OVERDUE = 'OVERDUE',
}

export type TTimelineItemLegacyType = {
  id: string;
  text: string;
  status: TTimelineItemStatusLegacy;
  isCompleted: boolean;
  disabled?: boolean;
  isHidden?: boolean;
  description?: string;
};

function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}

export type TimelineLegacyProps = {
  steps: TTimelineItemLegacyType[];
  setActiveStep: (itemIndex: number) => void;
  hideTail?: boolean;
  forceDisable?: boolean;
};

export type TimelineItemLegacyProps = {
  item: TTimelineItemLegacyType;
  lastFilledStepIndex: number;
  setActiveStep: (itemIndex: number) => void;
  index: number;
  hideTail?: boolean;
  forceDisable?: boolean;
  description?: string;
};

const TTimelineItemLegacy = ({
  item,
  hideTail = false,
  setActiveStep,
  index,
  lastFilledStepIndex,
  forceDisable = false,
  description,
}: TimelineItemLegacyProps) => {
  const { status, text, isCompleted, disabled, isHidden } = item;

  if (isHidden) return null;

  const icon =
    status === TTimelineItemStatusLegacy.COMPLETED || isCompleted ? (
      <CircleTickIcon className="text-good-to-go-100" />
    ) : status === TTimelineItemStatusLegacy.ACTIVE ? (
      <RadioCheckedIcon />
    ) : status === TTimelineItemStatusLegacy.OVERDUE ? (
      <div className="size-6 flex items-center justify-center rounded-full bg-error-100">
        <Icons.X className="text-text-white size-4" />
      </div>
    ) : (
      <RadioUncheckedIcon />
    );

  return (
    <div className="pb-6 last:pb-0 relative">
      <div
        className={classNames(
          'flex items-start gap-3',
          index <= lastFilledStepIndex + 1 && !disabled && !forceDisable
            ? 'cursor-pointer'
            : 'pointer-events-none'
        )}
        {...(!disabled && !forceDisable
          ? { onClick: () => setActiveStep(index) }
          : {})}
      >
        <div
          className={classNames(
            'flex flex-col items-center',
            !hideTail
              ? 'after:content-[""] after:absolute after:w-[1px] after:bg-stroke-2 after:top-6 after:bottom-0 after:left-[11px]'
              : ''
          )}
        >
          <div className="relative">{icon}</div>
        </div>
        <div className="flex flex-col gap-1">
          <span
            className={
              status === TTimelineItemStatusLegacy.ACTIVE
                ? 'body-14-medium text-neutral-1'
                : status === TTimelineItemStatusLegacy.COMPLETED || isCompleted
                  ? 'body-14-regular text-neutral-2'
                  : status === TTimelineItemStatusLegacy.OVERDUE
                    ? 'body-14-regular text-error-100'
                    : 'body-14-regular text-neutral-3'
            }
          >
            {text}
          </span>
          {description && (
            <span className="body-14-regular text-neutral-3">
              {description}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const TimelineLegacy = ({
  steps,
  setActiveStep,
  hideTail = false,
  forceDisable = false,
}: TimelineLegacyProps) => {
  if (!steps) return null;

  const lastFilledStepIndex = findLastIndex(steps, (step) => step.isCompleted);

  return (
    <div className="flex flex-col">
      {steps.map((step, idx) => (
        <TTimelineItemLegacy
          item={step}
          description={step.description}
          hideTail={hideTail || idx === steps.length - 1}
          key={step.id}
          setActiveStep={setActiveStep}
          index={idx}
          lastFilledStepIndex={lastFilledStepIndex}
          forceDisable={forceDisable}
        />
      ))}
    </div>
  );
};

export { TimelineLegacy };
